<template>
  <div>
    <div class="mt-4">
      <transition name="fade">
        <MenssageAlert :messages="messages" />
      </transition>
    </div>
    <CCol xs="12" sm="12">
      <div class="mb-4">
        <h2 class="font-weight-bold text-capitalize mb-3">
          {{ $t('usage-report.header') }}
        </h2>
        <p class="mb-0">
          {{ $t('usage-report.description') }}
        </p>
      </div>

      <CRow class="gx-1" :gutters="true">
        <CCol md="3">
          <label class="label-custom required">{{ $t('usage-report.select.time-period.label') }}</label>
          <form @submit.prevent>
            <v-date-picker
              v-model="timePeriodRange"
              color="purple"
              mode="range"
              timezone="UTC"
              :locale="lang"
              :min-date="timePeriodMinDate"
              :max-date="new Date()"
              is-required
              is-range
              addInputClasses="newInput"
              class="select"
            >
              <template v-slot="{ inputValue, inputEvents, showPopover, hidePopover }">
                <CInput
                  :value="rangeFormatted"
                  class="dateInputCustom newInput px-0 text-left inputAppendCustom inputAppendCustom--transparent"
                  add-input-classes="pr-0"
                  v-on="inputEvents"
                  readonly
                  style="min-width: 260px; background-color: white"
                  @mouseover="showPopover"
                  @mouseleave="hidePopover"
                >
                  <template #append-content>
                    <span @click="clearRange" class="pr-none">
                      <CIcon
                        v-if="inputValue"
                        class="btn-value mt-2 text-center"
                        name="cilXCircle"
                        size="custom-size"
                        :height="23"
                        viewBox="0 0 32 32"
                      />
                    </span>
                  </template>
                </CInput>
              </template>
            </v-date-picker>
          </form>
        </CCol>

        <CCol md="3" class="pr-3">
          <label class="label-custom">{{ $t('usage-report.select.user.label') }}</label>
          <v-select
            v-model="organizationUserSelect"
            class="no-close border-gray-300 rounded select"
            item-text="label"
            item-value="value"
            :searchable="true"
            :loading="isLoadingOrganizationUsers"
            :options="organizationUserSelectOptions"
            :disabled="isLoadingUsageReportData"
          />
        </CCol>
        <CCol md="6" class="pr-3" v-if="hasDescendentOrganizations">
          <div>
            <label class="label-custom">{{ $t('usage-report.select.organization.title') }}</label>
            <v-select
              v-model="organizationSelect"
              class="no-close border-gray-300 rounded select"
              item-text="label"
              item-value="value"
              :searchable="true"
              :options="organizationSelectOptions"
              :loading="isLoadingOrganizationDescendents"
              :disabled="isLoadingUsageReportData"
            />
          </div>
        </CCol>
      </CRow>

      <CRow class="mt-3" :gutters="true">
        <CCol md="3">
          <label class="label-custom">{{ $t('usage-report.select.channel.label') }}</label>
          <v-select
            v-model="channelSelect"
            class="no-close border-gray-300 rounded select"
            item-text="label"
            item-value="value"
            :searchable="true"
            :options="channelSelectOptions"
            :disabled="isLoadingUsageReportData"
            @input="handleChannelSelect"
          />
        </CCol>
        <CCol>
          <label class="label-custom">{{ $t('usage-report.select.sender-email.label') }}</label>
          <v-select
            v-model="emailSenderSelect"
            class="no-close border-gray-300 rounded select-sm"
            style="min-width: 140px"
            item-text="label"
            item-value="value"
            :searchable="true"
            :disabled="isLoadingUsageReportData || hasSMSSenderSelected || hasSMSChannelSelected"
            :loading="isLoadingEmailSenders"
            :options="emailSenderOptions"
          />
        </CCol>
        <CCol>
          <label class="label-custom">{{ $t('usage-report.select.sender-sms.label') }}</label>
          <v-select
            v-model="smsSenderSelect"
            class="no-close border-gray-300 rounded select-sm"
            style="min-width: 140px"
            item-text="label"
            item-value="value"
            :searchable="true"
            :disabled="isLoadingUsageReportData || hasEmailSenderSelected || hasEmailChannelSelected"
            :loading="isLoadingSMSSenders"
            :options="smsSenderOptions"
          />
        </CCol>
        <CCol md="3">
          <label class="label-custom">{{ $t('usage-report.filter.by-type-of-traffic') }}</label>
          <v-select
            v-model="typeOfTrafficSelect"
            class="no-close border-gray-300 rounded select-md"
            item-text="label"
            item-value="value"
            :searchable="true"
            :options="typeOfTrafficOptions"
            :disabled="isLoadingUsageReportData"
          />
        </CCol>
      </CRow>
      <hr class="mb-2" />
      <div class="mt-4">
        <CRow class="align-items-center">
          <CCol>
            <CButton color="primary" class="px-5 py-2 mr-3" @click="getUsageReportData" :disabled="isLoadingUsageReportData">
              <span class="font-1xl mr-1">{{ $t('usage-report.search') }}</span>
              <CIcon name="cilMagnifyingGlass" size="custom-size" :height="26" viewBox="0 0 32 32" />
            </CButton>

            <CButton color="primary" variant="outline" class="px-5 py-2 mr-3" @click="limparClick()" :disabled="isLoadingUsageReportData">
              <span class="font-1xl mr-1">{{ $t('usage-report.clear') }}</span>
            </CButton>

            <CButton
              v-if="hasTableRegisters"
              class="px-5 py-2 border-dark"
              size="md"
              color="white"
              @click="() => (showPopupExport = true)"
              :disabled="isLoadingUsageReportData"
            >
              <span class="font-1xl mr-1">{{ $t('usage-report.export') }}</span>
              <CIcon name="cil-file" class="export-icon" size="custom-size" :height="24" viewBox="0 0 30 30" />
            </CButton>
            <transition name="fade" v-if="showPopupExport">
              <div class="dataTable__popUp popUp" style="left: 64%" @click.stop.stop>
                <div class="popUp__body shadow-sm">
                  <ChooseExport
                    v-show="showPopupExport"
                    :isLoadingConfirm="isLoadingExportData"
                    @confirm="exportData"
                    @cancel="() => (showPopupExport = false)"
                  />
                </div>
              </div>
            </transition>
          </CCol>
          <CCol>
            <div class="d-flex gap-15 align-items-start justify-content-end">
              <div v-for="volumeCard in volumeCards" :key="volumeCard.id">
                <CCard class="mb-0 min-w-150 border-primary">
                  <CCardBody class="py-2">
                    <div>
                      <span class="font-weight-bold mr-2">{{ $t(`usage-report.card.${volumeCard.translationRef}`) }}</span>
                      <CIcon :name="volumeCard.iconName" class="icon-card-home-balance" size="custom-size" :height="20" viewBox="0 0 32 32" />
                    </div>
                    <CSpinner v-if="isLoadingUsageReportData" color="primary" style="width: 1rem; height: 1rem" />
                    <span v-else class="d-block">{{
                      usageReportData && usageReportData[volumeCard.totalProperty] ? formatNumber(usageReportData[volumeCard.totalProperty]) : 0
                    }}</span>
                  </CCardBody>
                </CCard>
              </div>
            </div>
          </CCol>
        </CRow>
      </div>

      <CCard class="mt-4">
        <CCardBody>
          <SenditTable
            :headers="tableHeaders"
            :registers="tableRegisters"
            :customClassesTable="senditTableClasses"
            :loading="isLoadingUsageReportData"
            translateHeader
          >
            <template #channel="props">
              <td class="text-center">{{ props.item.channel }}</td>
            </template>
            <template #sender="props">
              <td class="text-center">{{ props.item.sender }}</td>
            </template>
            <template #typeOfTraffic="props">
              <td class="text-center">{{ getTypeOfTraffic(props.item.campaignType) }}</td>
            </template>
            <template #total="props">
              <td class="text-center">{{ formatNumber(props.item.total) }}</td>
            </template>
            <template #footer-left>
              <div class="d-flex align-items-center h-100">
                <CIcon name="recover" size="custom-size" class="mr-2" :height="20" viewBox="0 0 32 32" />
                <span class="text-gray-700 font-weight-semi-bold">{{ $t('usage-report.warning.last-12-months') }}</span>
              </div>
            </template>
            <template #no-items>
              <CCol
                xs="12"
                sm="8"
                class="offset-sm-2 text-center emptyCard d-flex flex-column content-center justify-content-center py-3"
                style="min-height: 440px"
              >
                <div>
                  <figure class="mb-5">
                    <img src="/img/site/amico.png" alt="" width="181" />
                  </figure>
                  <h3 class="mb-3">
                    {{ $t('usage-report.table.empty.title') }}
                  </h3>
                  <div class="text-gray-700 text-justify">
                    <p>
                      {{ $t('usage-report.table.empty.description.001') }}
                    </p>
                    <span class="font-weight-bold">{{ $t('usage-report.table.empty.description.002.title') }}:</span>
                    <ul class="list-style-disc">
                      <li>{{ $t('usage-report.table.empty.description.002.item.001') }}</li>
                      <li>{{ $t('usage-report.table.empty.description.002.item.002') }}</li>
                    </ul>
                  </div>
                </div>
              </CCol>
            </template>
          </SenditTable>
        </CCardBody>
      </CCard>
    </CCol>
  </div>
</template>

<script>
import services from '@/services'
import { getErrorMessage } from '@/utils/errorHandling.js'
import MenssageAlert from '@/components/MenssageAlert'
import SenditTable from '@/components/table/SenditTable.vue'
import vSelect from 'vue-select'
import { formatNumber } from '@/utils/numbersFormatUtil.js'
import { v4 as uuidv4 } from 'uuid'
import { mapGetters } from 'vuex'
import ChooseExport from '@/components/ChooseExport'
import { getFilenameFromContentDisposition } from '@/utils/responseHeaderUtils'
import fileDownload from 'js-file-download'
import { dateFormatString, getUserDateFormat } from '@/utils/dateUtil.js'
import { DateTime } from 'luxon'

export default {
  name: 'UsageReport',
  components: { MenssageAlert, SenditTable, vSelect, ChooseExport },
  data() {
    return {
      usageReportData: undefined,
      isLoadingUsageReportData: false,
      messages: [],
      senditTableClasses: ['--overflow-hidden'],
      volumeCards: [
        {
          id: uuidv4(),
          iconName: 'email',
          translationRef: 'email',
          totalProperty: 'totalEmail',
        },
        {
          id: uuidv4(),
          translationRef: 'sms',
          iconName: 'sms',
          totalProperty: 'totalSms',
        },
      ],
      organizationDescendents: undefined,
      isLoadingOrganizationDescendents: false,
      organizationSelect: undefined,
      timePeriodRange: undefined,
      channelSelect: undefined,
      showPopupExport: false,
      isLoadingExportData: false,
      organizationUserSelect: undefined,
      organizationUsers: undefined,
      isLoadingOrganizationUsers: false,
      emailSenderSelect: undefined,
      emailSenders: undefined,
      isLoadingEmailSenders: false,
      smsSenderSelect: undefined,
      smsSenders: undefined,
      isLoadingSMSSenders: false,
      typeOfTrafficSelect: undefined,
    }
  },
  async mounted() {
    await this.setDefaultSelects()
    this.getOrganizationDescendents()
    // this.getUsageReportData()
    this.getOrganizationUsers()
    this.getEmailSenders()
    this.getSMSSenders()
  },
  methods: {
    limparClick() {
      this.setDefaultFilters()
      this.clearRange()
      this.usageReportData = null
    },
    clearRange() {
      const currentDate = new Date()

      const startDate = new Date(currentDate)
      startDate.setDate(currentDate.getDate() - 30)

      this.timePeriodRange = {
        start: startDate,
        end: currentDate,
      }
    },
    handleChannelSelect() {
      const hasEmailChannelAndSmsSelected = this.hasEmailChannelSelected && this.hasSMSSenderSelected
      const hasSmsChannelAndEmailSelected = this.hasSMSChannelSelected && this.hasEmailSenderSelected

      if (this.channelSelect.value === null || hasEmailChannelAndSmsSelected || hasSmsChannelAndEmailSelected) {
        this.selectDefaultSenders()
      }
      // this.getUsageReportData()
    },
    async getUsageReportData() {
      try {
        this.isLoadingUsageReportData = true

        const params = {
          OrganizationId: this.organizationSelect.value,
          Startdate: this.timePeriodRange.start?.toISOString().split('T')[0],
          EndDate: this.timePeriodRange.end?.toISOString().split('T')[0],
        }

        if (this.channelSelect.value) {
          params.Channel = this.channelSelect.value
        }

        if (this.organizationUserSelect.value) {
          params.OrganizationUserId = this.organizationUserSelect.value
        }

        if (this.emailSenderSelect.value) {
          params.SenderDomain = this.emailSenderSelect.value
        }

        if (this.smsSenderSelect.value) {
          params.SenderSms = this.smsSenderSelect.value
        }

        if (this.typeOfTrafficSelect.value) {
          params.CampaignType = this.typeOfTrafficSelect.value
        }

        const response = await services.getOrganizationConsumptions(this.$route.params.organizationId, params)

        this.usageReportData = response.data.data
      } catch (error) {
        this.messages = [getErrorMessage(error?.response?.data)]
      } finally {
        this.isLoadingUsageReportData = false
      }
    },
    async getOrganizationDescendents() {
      try {
        this.isLoadingOrganizationDescendents = true

        const response = await services.getSubOrganizations(this.$route.params.organizationId, 0, 9999)

        this.organizationDescendents = response.data.data
      } catch (error) {
        this.messages = [getErrorMessage(error?.response?.data)]
      } finally {
        this.isLoadingOrganizationDescendents = false
      }
    },
    async getOrganizationUsers() {
      try {
        this.isLoadingOrganizationUsers = true
        const response = await services.getCampaignOrganizationUsers(this.$route.params.organizationId)

        this.organizationUsers = response.data.data
      } finally {
        this.isLoadingOrganizationUsers = false
      }
    },
    setDefaultFilters() {
      this.channelSelect = this.channelSelectOptions[0]

      this.organizationUserSelect = this.organizationUserSelectOptions[0]

      this.emailSenderSelect = this.emailSenderOptions[0]
      this.smsSenderSelect = this.smsSenderOptions[0]
      this.typeOfTrafficSelect = this.typeOfTrafficOptions[0]
    },
    setDefaultSelects() {
      return new Promise((resolve) => {
        this.organizationSelect = this.currentOrganizationOption

        const currentDate = new Date()

        const startDate = new Date(currentDate)
        startDate.setDate(currentDate.getDate() - 30)

        this.timePeriodRange = {
          start: startDate,
          end: currentDate,
        }

        this.setDefaultFilters()

        resolve()
      })
    },
    async exportData(exportConfig) {
      try {
        this.isLoadingExportData = true

        const params = {
          OrganizationId: this.organizationSelect.value,
          Startdate: this.timePeriodRange.start.toISOString().split('T')[0],
          EndDate: this.timePeriodRange.end.toISOString().split('T')[0],
          format: exportConfig.format,
        }

        if (exportConfig.delimiter) {
          params.delimiter = exportConfig.delimiter
        }

        if (this.channelSelect.value) {
          params.Channel = this.channelSelect.value
        }

        if (this.organizationUserSelect.value) {
          params.OrganizationUserId = this.organizationUserSelect.value
        }

        if (this.emailSenderSelect.value) {
          params.SenderDomain = this.emailSenderSelect.value
        }

        if (this.smsSenderSelect.value) {
          params.SenderSms = this.smsSenderSelect.value
        }

        if (this.typeOfTrafficSelect.value) {
          params.CampaignType = this.typeOfTrafficSelect.value
        }

        const response = await services.downloadOrganizationConsumptions(this.$route.params.organizationId, params)
        const nameFile = getFilenameFromContentDisposition(response.headers['content-disposition'])
        fileDownload(response.data, nameFile, response.headers['content-type'])
      } catch (error) {
        this.messages = [getErrorMessage(error?.response?.data)]
      } finally {
        this.showPopupExport = false
        this.isLoadingExportData = false
      }
    },
    async getEmailSenders() {
      try {
        this.isLoadingEmailSenders = true
        const organizationId = this.$route.params.organizationId

        const response = await services.getSendersDomainByOrganizationUserId(organizationId)
        this.emailSenders = response.data.data
      } catch (error) {
        this.messages = [getErrorMessage(error?.response?.data)]
      } finally {
        this.isLoadingEmailSenders = false
      }
    },
    async getSMSSenders() {
      try {
        this.isLoadingSMSSenders = true
        const organizationId = this.$route.params.organizationId
        const response = await services.getSendersByOrganizationUserId(organizationId)

        this.smsSenders = response.data.data
      } catch (error) {
        this.messages = [getErrorMessage(error?.response?.data)]
      } finally {
        this.isLoadingSMSSenders = false
      }
    },
    getTypeOfTraffic(campaignType) {
      return this.typeOfTrafficOptions.find((option) => option.value === campaignType)?.label || ''
    },
    formatNumber(value) {
      return formatNumber(value)
    },
    clearFilters() {
      this.setDefaultFilters()
      this.getUsageReportData()
    },
    selectSMSChannel() {
      this.channelSelect = this.channelSelectOptions[2]
    },
    selectEmailChannel() {
      this.channelSelect = this.channelSelectOptions[1]
    },
    selectDefaultSenders() {
      this.smsSenderSelect = this.smsSenderOptions[0]
      this.emailSenderSelect = this.emailSenderOptions[0]
    },
  },
  computed: {
    rangeFormatted() {
      if (!this.hasRange) return null

      const userDateTimeFormat = getUserDateFormat(this.$store.state.auth.user.dateTimeFormat)

      const formattedStart = DateTime.fromJSDate(this.timePeriodRange.start).setZone('UTC').toFormat(userDateTimeFormat)
      const formattedEnd = DateTime.fromJSDate(this.timePeriodRange.end).setZone('UTC').toFormat(userDateTimeFormat)

      return `${formattedStart} - ${formattedEnd}`
    },
    hasRange() {
      return Boolean(this.timePeriodRange && this.timePeriodRange.start && this.timePeriodRange.end)
    },
    typeOfTrafficOptions() {
      return [
        { value: null, label: this.$t('usage-report.type-of-traffic.all') },
        { value: 'MKT', label: this.$t('usage-report.type-of-traffic.campaign') },
        { value: 'TRANSACTIONAL', label: this.$t('usage-report.type-of-traffic.transactions') },
        { value: 'AUTOMATION', label: this.$t('usage-report.type-of-traffic.automation') },
      ]
    },
    tableRegisters() {
      return this.usageReportData?.results
    },
    tableHeaders() {
      return [
        { key: 'userName', label: this.$t('usage-report.table.header.002'), sorter: false },
        { key: 'channel', label: this.$t('usage-report.table.header.003'), sorter: false, _classes: 'text-center' },
        { key: 'sender', label: this.$t('usage-report.table.header.005'), sorter: false, _classes: 'text-center' },
        { key: 'typeOfTraffic', label: this.$t('usage-report.table.header.006'), sorter: false, _classes: 'text-center' },
        { key: 'total', label: this.$t('usage-report.table.header.004'), sorter: false, _classes: 'text-center' },
      ]
    },
    userId() {
      return this.$store.state.auth.user.id
    },
    timePeriodRangeFormatted() {
      if (!this.timePeriodRange) return null

      const userDateTimeFormat = getUserDateFormat(this.$store.state.auth.user.dateTimeFormat)

      const formattedStart = dateFormatString(this.timePeriodRange.start.toISOString(), '', userDateTimeFormat)
      const formattedEnd = dateFormatString(this.timePeriodRange.end.toISOString(), '', userDateTimeFormat)

      return {
        start: formattedStart,
        end: formattedEnd,
      }
    },
    timePeriodMinDate() {
      const minDate = new Date()
      minDate.setFullYear(minDate.getFullYear() - 1)
      minDate.setDate(minDate.getDate() + 1)
      return minDate
    },
    currentOrganizationOption() {
      return { label: this.$store.state.auth.session.organization.name, value: this.$route.params.organizationId }
    },
    organizationSelectOptions() {
      if (!this.organizationDescendents?.items) return [this.currentOrganizationOption]

      const organizationDescendentsOptions = this.organizationDescendents.items.map((elm) => {
        return {
          label: elm.companyName,
          value: elm.organizationId,
        }
      })

      return [this.currentOrganizationOption, ...organizationDescendentsOptions]
    },
    hasDescendentOrganizations() {
      return this.organizationDescendents?.items.length > 0
    },
    channelSelectOptions() {
      return [
        {
          label: this.$t('usage-report.channel-select.all'),
          value: null,
        },
        {
          label: 'Email',
          value: 1,
        },
        {
          label: 'SMS',
          value: 2,
        },
      ]
    },
    organizationUserSelectOptions() {
      const allUsersOption = {
        label: this.$t('usage-report.users-select.all'),
        value: null,
      }

      if (!this.organizationUsers) return [allUsersOption]

      const organizationUsersOptions = this.organizationUsers.map((elm) => {
        return {
          label: elm.name,
          value: elm.organizationUserId,
        }
      })

      return [allUsersOption, ...organizationUsersOptions]
    },
    emailSenderOptions() {
      const allSendersOption = {
        label: this.$t('usage-report.email-senders.all'),
        value: null,
      }

      if (!this.emailSenders) return [allSendersOption]

      const emailSenderOptions = this.emailSenders.map((elm) => {
        return { value: elm.sender, label: '@' + elm.sender }
      })

      return [allSendersOption, ...emailSenderOptions]
    },
    smsSenderOptions() {
      const allSendersOption = {
        label: this.$t('usage-report.sms-senders.all'),
        value: null,
      }

      if (!this.smsSenders) return [allSendersOption]

      const smsSendersOptions = this.smsSenders.map((elm) => {
        return { value: elm.sender, label: elm.sender }
      })

      return [allSendersOption, ...smsSendersOptions]
    },
    hasEmailSenderSelected() {
      return Boolean(this.emailSenderSelect && this.emailSenderSelect.value)
    },
    hasEmailChannelSelected() {
      return Boolean(this.channelSelect?.value === this.channelSelectOptions[1].value)
    },
    hasSMSSenderSelected() {
      return Boolean(this.smsSenderSelect && this.smsSenderSelect.value)
    },
    hasSMSChannelSelected() {
      return Boolean(this.channelSelect?.value === this.channelSelectOptions[2].value)
    },
    hasTableRegisters() {
      return this.tableRegisters && this.tableRegisters.length > 0
    },
    ...mapGetters({
      lang: 'getLang',
    }),
  },
  watch: {
    smsSenderSelect(option) {
      if (option.value !== null) {
        this.selectSMSChannel()
      }
    },
    emailSenderSelect(option) {
      if (option.value !== null) {
        this.selectEmailChannel()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.min-w-150 {
  min-width: 150px;
}

.gap-15 {
  gap: 15px;
}

.flex-1 {
  flex: 1;
}

.select {
  min-width: 227px;
}

.select-md {
  min-width: 220px;
}

.select-sm {
  min-width: 165px;
}

.list-style-disc {
  list-style: disc;
}

.border-2-primary {
  border: 2px solid var(--primary) !important;
}

.export-btn {
  color: var(--blue-link);
  font-size: 16px;

  &:hover {
    text-decoration: underline;
  }
}

.export-icon {
  height: 20px;
  width: 20px;
}
</style>
<style>
.dateInputCustom .form-control[readonly] {
  background-color: white !important;
}
</style>
